import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiMessageCircle, FiX, FiSend, FiUser, FiHelpCircle, FiClock,
  FiCheckCircle, FiArrowRight, FiSearch, FiBook, FiShield,
  FiSettings, FiUsers, FiPackage, FiCreditCard, FiAlertCircle
} = FiIcons;

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState('welcome');
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const { user } = useApp();

  // Smart responses based on user type and context
  const getSmartResponses = () => {
    const userType = user?.type || 'visitor';
    const currentPage = location.pathname;
    
    const responses = {
      visitor: {
        welcome: [
          { text: "👋 Hi! I'm here to help you navigate AB Orders.", type: 'bot' },
          { text: "I can guide you through placing orders, payment options, or collection procedures. What would you like to know?", type: 'bot' }
        ],
        quickActions: [
          { text: "📋 How to place my first order", action: 'first-order' },
          { text: "💳 Payment methods & security", action: 'payment' },
          { text: "📍 Collecting my order at prison", action: 'collection' },
          { text: "✏️ Modifying or cancelling orders", action: 'modify' },
          { text: "❓ Common questions", action: 'faq' }
        ]
      },
      prison: {
        welcome: [
          { text: "👋 Hello! I'm your AB Orders assistant for prison staff.", type: 'bot' },
          { text: "I can help with inventory management, order processing, or system operations. How can I assist?", type: 'bot' }
        ],
        quickActions: [
          { text: "📦 Managing inventory & stock", action: 'inventory' },
          { text: "🔄 Processing visitor orders", action: 'orders' },
          { text: "⚙️ Daily operations guide", action: 'operations' },
          { text: "🛡️ Security protocols", action: 'security' },
          { text: "📊 Reports & analytics", action: 'reports' }
        ]
      },
      admin: {
        welcome: [
          { text: "👋 Welcome! I'm your AB Orders system administrator assistant.", type: 'bot' },
          { text: "I can help with system management, financial reports, or facility oversight. What do you need?", type: 'bot' }
        ],
        quickActions: [
          { text: "🏛️ Managing prison facilities", action: 'facilities' },
          { text: "📈 Financial reports & analytics", action: 'finance' },
          { text: "👥 User management", action: 'users' },
          { text: "🔧 System monitoring", action: 'monitoring' },
          { text: "🛡️ Security & compliance", action: 'compliance' }
        ]
      }
    };

    return responses[userType] || responses.visitor;
  };

  // Context-aware suggestions based on current page
  const getContextualSuggestions = () => {
    const path = location.pathname;
    
    if (path.includes('/menu')) {
      return [
        { text: "How do I add items to cart?", action: 'add-cart' },
        { text: "What if an item is out of stock?", action: 'out-stock' },
        { text: "Can I see nutritional information?", action: 'nutrition' }
      ];
    }
    
    if (path.includes('/cart')) {
      return [
        { text: "How do I modify quantities?", action: 'modify-qty' },
        { text: "What payment methods are accepted?", action: 'payment' },
        { text: "When will my order be ready?", action: 'timing' }
      ];
    }
    
    if (path.includes('/orders')) {
      return [
        { text: "How to track order status?", action: 'track' },
        { text: "Can I cancel an order?", action: 'cancel' },
        { text: "What if I miss my collection time?", action: 'missed' }
      ];
    }

    if (path.includes('/inventory')) {
      return [
        { text: "How to add new menu items?", action: 'add-item' },
        { text: "Setting up stock alerts?", action: 'alerts' },
        { text: "Managing supplier information?", action: 'suppliers' }
      ];
    }

    return [];
  };

  // Smart response generator
  const generateResponse = (action) => {
    const responses = {
      'first-order': [
        { text: "Great! Here's how to place your first order:", type: 'bot' },
        { text: "1️⃣ Select the prison you're visiting\n2️⃣ Enter your visit details and prisoner information\n3️⃣ Browse the menu and add items to cart\n4️⃣ Review and pay securely\n5️⃣ Collect at the prison", type: 'bot' },
        { text: "💡 Tip: Orders must be placed at least 24 hours before your visit!", type: 'bot' }
      ],
      'payment': [
        { text: "💳 We accept all major payment methods:", type: 'bot' },
        { text: "✅ Debit/Credit Cards (Visa, Mastercard, Amex)\n✅ Digital Wallets (PayPal, Apple Pay, Google Pay)\n✅ Bank Transfers for larger orders", type: 'bot' },
        { text: "🔒 All payments use 256-bit SSL encryption and are PCI DSS compliant for maximum security.", type: 'bot' }
      ],
      'collection': [
        { text: "📍 Here's how to collect your order:", type: 'bot' },
        { text: "1️⃣ Arrive 15 minutes early\n2️⃣ Present ID and order confirmation at reception\n3️⃣ Complete security checks\n4️⃣ Proceed to AB Orders collection point\n5️⃣ Verify and sign for your items", type: 'bot' },
        { text: "⏰ Orders are held for 2 hours past your visit time.", type: 'bot' }
      ],
      'modify': [
        { text: "✏️ Order modification timelines:", type: 'bot' },
        { text: "📝 Full modifications: Up to 48 hours before visit\n➕ Add items: Up to 24 hours before\n➖ Remove items: Up to 24 hours before\n🚨 Emergency changes: Contact prison directly", type: 'bot' },
        { text: "💰 Refunds are processed within 3-5 business days.", type: 'bot' }
      ],
      'inventory': [
        { text: "📦 Inventory management best practices:", type: 'bot' },
        { text: "🌅 Check stock levels every morning\n📊 Update quantities in real-time\n🔔 Set minimum stock alerts\n📅 Monitor expiry dates daily\n📋 Complete end-of-day reconciliation", type: 'bot' },
        { text: "💡 Use the analytics dashboard to identify popular items and optimize your menu!", type: 'bot' }
      ],
      'orders': [
        { text: "🔄 Order processing workflow:", type: 'bot' },
        { text: "⏰ Orders appear immediately after payment\n✅ Review every 2 hours during business hours\n🍳 Begin preparation 4 hours before visit\n📦 Complete packaging 2 hours before\n🔍 Final quality checks 1 hour before", type: 'bot' },
        { text: "📋 Always verify items against confirmation before release!", type: 'bot' }
      ],
      'faq': [
        { text: "❓ Most common questions:", type: 'bot' },
        { text: "🔐 Login issues? Clear cache and try 'Forgot Password'\n💳 Payment declined? Check card details and limits\n📍 Can't find your prison? Not all facilities participate yet\n⏰ Late arrival? Orders held for 2 hours past visit time", type: 'bot' },
        { text: "📞 Need more help? Call 0800-AB-ORDERS or email support@ab-orders.com", type: 'bot' }
      ]
    };

    return responses[action] || [
      { text: "I understand your question. Let me help you find the right information.", type: 'bot' },
      { text: "You can find detailed guides in our 'How It Works' section, or I can connect you with our support team.", type: 'bot' }
    ];
  };

  // Initialize chat based on user and context
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const smartResponses = getSmartResponses();
      setIsTyping(true);
      
      setTimeout(() => {
        setMessages(smartResponses.welcome);
        setIsTyping(false);
        
        // Add quick actions after welcome
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            { text: "Here are some quick actions to get you started:", type: 'bot' },
            { type: 'actions', actions: smartResponses.quickActions }
          ]);
        }, 1000);
      }, 1000);
    }
  }, [isOpen, user]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = { text: inputValue, type: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simple keyword matching for smart responses
    const query = inputValue.toLowerCase();
    let responseAction = 'faq';

    if (query.includes('order') || query.includes('place')) responseAction = 'first-order';
    if (query.includes('payment') || query.includes('pay')) responseAction = 'payment';
    if (query.includes('collect') || query.includes('pickup')) responseAction = 'collection';
    if (query.includes('cancel') || query.includes('modify')) responseAction = 'modify';
    if (query.includes('inventory') || query.includes('stock')) responseAction = 'inventory';

    setTimeout(() => {
      const response = generateResponse(responseAction);
      setMessages(prev => [...prev, ...response]);
      setIsTyping(false);
    }, 1500);
  };

  const handleQuickAction = (action) => {
    setIsTyping(true);
    
    setTimeout(() => {
      const response = generateResponse(action);
      setMessages(prev => [...prev, ...response]);
      setIsTyping(false);

      // Add contextual suggestions
      const contextSuggestions = getContextualSuggestions();
      if (contextSuggestions.length > 0) {
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            { text: "Based on where you are, you might also want to know:", type: 'bot' },
            { type: 'actions', actions: contextSuggestions }
          ]);
        }, 1000);
      }
    }, 1000);
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 z-50 bg-primary-500 hover:bg-primary-600 text-white p-4 rounded-full shadow-xl transition-all duration-300 hover:scale-110"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2 }}
      >
        <SafeIcon icon={isOpen ? FiX : FiMessageCircle} className="w-6 h-6" />
        
        {/* Notification Badge */}
        {!isOpen && (
          <motion.div
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 3 }}
          >
            !
          </motion.div>
        )}
      </motion.button>

      {/* Chat Widget */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-24 left-6 z-40 bg-white dark:bg-government-800 rounded-2xl shadow-2xl w-96 h-[500px] flex flex-col overflow-hidden border border-government-200 dark:border-government-600"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">AB Orders Assistant</h3>
                    <p className="text-xs opacity-90">Here to help 24/7</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message, index) => (
                <div key={index}>
                  {message.type === 'bot' ? (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-start space-x-2"
                    >
                      <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiHelpCircle} className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="bg-government-50 dark:bg-government-700 rounded-2xl rounded-tl-md p-3 max-w-[280px]">
                        <p className="text-government-900 dark:text-white text-sm whitespace-pre-line">
                          {message.text}
                        </p>
                      </div>
                    </motion.div>
                  ) : message.type === 'user' ? (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex justify-end"
                    >
                      <div className="bg-primary-500 text-white rounded-2xl rounded-tr-md p-3 max-w-[280px]">
                        <p className="text-sm">{message.text}</p>
                      </div>
                    </motion.div>
                  ) : message.type === 'actions' ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-2"
                    >
                      {message.actions.map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          onClick={() => handleQuickAction(action.action)}
                          className="w-full text-left p-3 bg-white dark:bg-government-800 border border-government-200 dark:border-government-600 rounded-xl hover:bg-government-50 dark:hover:bg-government-700 transition-colors text-sm"
                        >
                          {action.text}
                        </button>
                      ))}
                    </motion.div>
                  ) : null}
                </div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start space-x-2"
                >
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiHelpCircle} className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="bg-government-50 dark:bg-government-700 rounded-2xl rounded-tl-md p-3">
                    <div className="flex space-x-1">
                      <motion.div
                        className="w-2 h-2 bg-government-400 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                      />
                      <motion.div
                        className="w-2 h-2 bg-government-400 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                      />
                      <motion.div
                        className="w-2 h-2 bg-government-400 rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-government-200 dark:border-government-600">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your question..."
                  className="flex-1 px-4 py-2 border border-government-300 dark:border-government-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white transition-colors"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim()}
                  className="p-2 bg-primary-500 hover:bg-primary-600 disabled:bg-government-300 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
                >
                  <SafeIcon icon={FiSend} className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex items-center justify-center mt-2 text-xs text-government-500 dark:text-government-400">
                <span>Powered by AB Orders Smart Assistant</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default ChatWidget;